import useCreateDocumentTemplate from '@/api/mutations/documentTemplate/useCreateDocumentTemplate';
import useUpdateDocumentTemplate from '@/api/mutations/documentTemplate/useUpdateDocumentTemplate';
import { DocumentTemplate } from '@/api/queries/me/useGetDocumentTemplate';
import AutoSaveForm, {
  type AutoSaveFormHandle,
} from '@/ui/components/autoSaveForm/AutoSaveForm';
import DocumentTemplatePreview from '@/ui/components/documentTemplatePreview/DocumentTemplatePreview';
import UploadTemplateField from '@/ui/components/uploadTemplateField/UploadTemplateField';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ButtonRadioGroup,
  Card,
  Checkbox,
  Field,
  RawInput,
  Tabs,
  Textarea,
  useToastContext,
  H2,
  Text,
} from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import { downloadHtmlFile } from '@payaca/utilities/fileUtilities';
import { Nullish } from '@payaca/utilities/types';
import { expectedSignatoriesInputSchema } from '@payaca/validation/schemas';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Controller,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { z } from 'zod/v4';
import { AvailableDocumentTemplateVariablesList } from './AvailableDocumentTemplateVariablesList';

const FORM_SCHEMA = z
  .object({
    name: z.string().min(1, 'Name is required').max(1023),
    description: z.string().max(4095).optional().nullable(),
    templateHtml: z
      .object({
        preview: z.string(),
        raw: z.string(),
        usedVariables: z.array(z.string()),
      })
      .nullable()
      .refine((val) => val !== null, {
        message: 'Template HTML is required',
      }),
    expectedSignatories: expectedSignatoriesInputSchema.nullish(),
  })
  .refine(
    (data) => {
      const accessedSignatures = new Set(
        data.templateHtml?.usedVariables
          .filter((p) => p.startsWith('signatures.'))
          .map((p) => p.split('.')[1]) // second segment
      );

      const availableSignatures = new Set(
        data.expectedSignatories?.map((signatory) => signatory.identifier)
      );

      const unavailableSignatures =
        accessedSignatures.difference(availableSignatures);

      return unavailableSignatures.size === 0;
    },
    {
      path: ['expectedSignatories'],
      message:
        'The provided template uses signature variables, so this document must require a customer signature.',
    }
  )
  .refine(
    (data) => {
      const availableSignatures = data.expectedSignatories?.map(
        (signatory) => signatory.identifier
      );

      if (!availableSignatures?.length) return true;

      const allAvailableSignaturesAreUsed = availableSignatures?.every((sig) =>
        data.templateHtml?.usedVariables.includes(
          `signatures.${sig}.signatureImageUrl`
        )
      );

      return allAvailableSignaturesAreUsed;
    },
    {
      path: ['expectedSignatories'],
      message: `A customer signature is required, but the provided template doesn't include the signature`,
    }
  );

type TFormSchema = z.infer<typeof FORM_SCHEMA>;

const getDefaultValues = (template: Nullish<DocumentTemplate>) => {
  if (!template) return { templateHtml: null };

  return {
    name: template.name,
    description: template.description,
    templateHtml: {
      raw: template.templateHtml,
      preview: template.previewDummyDocumentInstance.documentHtml,
      usedVariables: template.usedVariables,
    },
    expectedSignatories: template.expectedSignatories || null,
  };
};

/**
 * The content that round-trips through a save. Comparing it against the server
 * template is how we tell one of our own saves echoing back from a change made
 * elsewhere (Agent Dave editing from the chat panel).
 */
type TemplateContent = {
  name: string | undefined;
  description: string | Nullish<string>;
  templateHtml: string | null;
  expectedSignatories: TFormSchema['expectedSignatories'];
};

const serialiseContent = (content: TemplateContent): string =>
  JSON.stringify({
    name: content.name ?? null,
    description: content.description ?? null,
    templateHtml: content.templateHtml ?? null,
    expectedSignatories:
      content.expectedSignatories?.map((signatory) => ({
        role: signatory.role,
        identifier: signatory.identifier,
      })) ?? null,
  });

const serverContentKey = (template: DocumentTemplate): string =>
  serialiseContent({
    name: template.name,
    description: template.description,
    templateHtml: template.templateHtml,
    expectedSignatories: template.expectedSignatories,
  });

const formContentKey = (data: TFormSchema): string =>
  serialiseContent({
    name: data.name,
    description: data.description,
    templateHtml: data.templateHtml?.raw ?? null,
    expectedSignatories: data.expectedSignatories,
  });

export const CreateEditDocumentTemplateForm: FC<{
  template: Nullish<DocumentTemplate>;
  /** Called with the new template's ID when auto-save first creates it. */
  onCreated: (templateId: string) => void;
}> = (props) => {
  const { template, onCreated } = props;

  const [isMobileView, setIsMobileView] = useState(false);
  // Preview tab for an existing template, Config tab for a new one.
  const [selectedTabIndex, setSelectedTabIndex] = useState(template ? 0 : 1);

  const formMethods = useForm<TFormSchema>({
    defaultValues: getDefaultValues(template),
    resolver: zodResolver(FORM_SCHEMA),
  });
  const templateHtml = formMethods.watch('templateHtml');

  const { mutateAsync: createDocumentTemplate } = useCreateDocumentTemplate();
  const { mutateAsync: updateDocumentTemplate } = useUpdateDocumentTemplate();

  // Once auto-save has created the template, later saves must update it —
  // even before the navigation to the edit route remounts the form.
  const createdTemplateIdRef = useRef<string | null>(null);
  const isCreatingRef = useRef(false);

  const autoSaveRef = useRef<AutoSaveFormHandle>(null);

  // Content this form has sent to the server whose refetch we haven't yet seen
  // return, as a multiset (content key -> count). A save invalidates the query
  // the form reads, so every save triggers a refetch; several can be in flight
  // and their refetches can arrive carrying any of the intermediate values.
  const inFlightSavesRef = useRef(new Map<string, number>());

  // Keep the editor in step with server-side edits. Agent Dave can change the
  // template from the chat panel; when he does, the refetched template is a
  // value we never sent, so adopt it. A refetch carrying one of our own
  // in-flight saves is skipped, so edits the user has typed since that save are
  // preserved. On a genuine mismatch Dave's version wins.
  useEffect(() => {
    if (!template) return;

    const serverKey = serverContentKey(template);
    const inFlight = inFlightSavesRef.current;

    const echoCount = inFlight.get(serverKey);
    if (echoCount) {
      // Our own save echoing back — the form may already hold newer edits.
      inFlight.set(serverKey, echoCount - 1);
      return;
    }

    if (serverKey === formContentKey(formMethods.getValues())) return;

    formMethods.reset(getDefaultValues(template));
    // The reset re-emits form values, which would otherwise schedule an
    // auto-save that writes Dave's just-adopted content straight back.
    autoSaveRef.current?.cancel();
  }, [template, formMethods]);

  const { pushError } = useToastContext();

  const handleAutoSave: SubmitHandler<TFormSchema> = async (data) => {
    if (!data.templateHtml) {
      // We won't get here due to zod refinement, but TS doesn't know that
      return;
    }

    const payload = {
      name: data.name,
      description: data.description,
      templateHtml: data.templateHtml.raw,
      expectedSignatories: data.expectedSignatories,
    };

    // Record what we're sending so its refetch is recognised as our own edit
    // rather than an external one.
    const sentKey = formContentKey(data);
    const inFlight = inFlightSavesRef.current;
    inFlight.set(sentKey, (inFlight.get(sentKey) ?? 0) + 1);

    try {
      const existingId = template?.id ?? createdTemplateIdRef.current;
      if (existingId) {
        await updateDocumentTemplate({ id: existingId, ...payload });
      } else {
        if (isCreatingRef.current) return;
        isCreatingRef.current = true;
        const result = await createDocumentTemplate(payload);
        createdTemplateIdRef.current = result.createDocumentTemplate.id;
        onCreated(result.createDocumentTemplate.id);
      }
    } catch (e) {
      // No refetch will carry this value, so stop expecting its echo.
      inFlight.set(sentKey, Math.max(0, (inFlight.get(sentKey) ?? 1) - 1));
      pushError('Something went wrong saving the template');
    } finally {
      isCreatingRef.current = false;
    }
  };

  return (
    <AutoSaveForm
      ref={autoSaveRef}
      methods={formMethods}
      onSubmit={handleAutoSave}
      className="h-full"
    >
      <div className="bg-gray-50 size-full overflow-y-auto">
        <div className="p-4">
          <Tabs
            selectedIndex={selectedTabIndex}
            onChange={setSelectedTabIndex}
            // Always pass a suffix node: with no suffix the Tabs component
            // moves className onto the tab list itself, where justify-between
            // spreads the tabs apart.
            suffix={
              <span
                className={
                  selectedTabIndex === 0 && templateHtml
                    ? 'visible'
                    : 'invisible'
                }
              >
                <PreviewDeviceControl
                  isMobileView={isMobileView}
                  onChange={setIsMobileView}
                />
              </span>
            }
            className="justify-between"
          >
            <Tabs.Panel
              label="Preview"
              render={() => <PreviewPanel isMobileView={isMobileView} />}
            />
            <Tabs.Panel label="Config" render={() => <ConfigPanel />} />
            <Tabs.Panel label="Code" render={() => <CodePanel />} />
          </Tabs>
        </div>
      </div>
    </AutoSaveForm>
  );
};

const PreviewDeviceControl: FC<{
  isMobileView: boolean;
  onChange: (isMobile: boolean) => void;
}> = (props) => {
  const { isMobileView, onChange } = props;

  const DeviceOption = useCallback(
    ({
      option: { label, value },
    }: {
      option: { label: string; value: boolean };
    }) => {
      return (
        <>
          <UntitledIcon
            className="w-5 h-5"
            name={value ? 'phone-01.3' : 'monitor-01.3'}
          />
          {label}
        </>
      );
    },
    []
  );

  return (
    <ButtonRadioGroup
      value={isMobileView}
      sizeVariant="sm"
      onChange={onChange}
      options={[
        {
          label: 'Desktop',
          value: false,
        },
        {
          label: 'Mobile',
          value: true,
        },
      ]}
      CustomOption={DeviceOption}
    />
  );
};

const ConfigPanel: FC = () => {
  const formMethods = useFormContext<TFormSchema>();

  return (
    <Card>
      <Card.Body>
        <div className="flex flex-col gap-4">
          <Field name={'name'}>
            <Field.Label>Template name</Field.Label>
            <Field.HelperText>
              This will be the default name of any generated Documents
            </Field.HelperText>
            <RawInput {...formMethods.register('name')} />
          </Field>
          <Controller
            name="description"
            render={({ field }) => {
              return (
                <Field name={field.name}>
                  <Field.Label>Template description</Field.Label>
                  <Field.HelperText>
                    Help people understand what this document is for
                  </Field.HelperText>
                  <Textarea
                    value={field.value}
                    onChange={field.onChange}
                    rows={3}
                  />
                </Field>
              );
            }}
          />
          {import.meta.env.VITE_ENABLE_DOCUMENT_SIGNATORIES === 'true' && (
            <Controller
              name="expectedSignatories"
              render={({ field }) => {
                return (
                  <Field name={field.name}>
                    <Checkbox
                      label="Require customer signature"
                      checked={!!field.value?.length}
                      onChange={(checked) => {
                        if (!field.value?.length) {
                          field.onChange([
                            {
                              role: 'customer',
                              identifier: 'customer',
                            },
                          ]);
                        } else {
                          field.onChange(null);
                        }
                      }}
                    />
                  </Field>
                );
              }}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const PreviewPanel: FC<{
  isMobileView: boolean;
}> = (props) => {
  const { isMobileView } = props;
  const formMethods = useFormContext<TFormSchema>();
  const templateHtml = formMethods.watch('templateHtml');

  if (!templateHtml) {
    return (
      <Card>
        <Card.Body>
          <div className="text-center py-12 space-y-1.5">
            <H2>No template yet</H2>
            <Text colour="muted">
              Add the template HTML in the Code tab to see a preview
            </Text>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <DocumentTemplatePreview
      html={templateHtml.preview}
      isMobile={isMobileView}
    />
  );
};

const CodePanel: FC = () => {
  const formMethods = useFormContext<TFormSchema>();
  const templateHtml = formMethods.watch('templateHtml');
  const name = formMethods.watch('name');
  const expectedSignatories = formMethods.watch('expectedSignatories');

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Controller<TFormSchema, 'templateHtml'>
              name="templateHtml"
              render={({ field: { value, onChange, name } }) => {
                return (
                  <UploadTemplateField
                    variant="entryOnly"
                    name={name}
                    value={value}
                    onChange={(template) => {
                      const expectedSignatories =
                        formMethods.getValues().expectedSignatories;

                      if (
                        template?.usedVariables.some((variable) =>
                          variable.startsWith('signatures.customer')
                        ) &&
                        !expectedSignatories?.some(
                          (signatory) => signatory.identifier === 'customer'
                        )
                      ) {
                        // The user has uploaded a template containing customer signature variables,
                        // but hasn't indicated that a customer signature is expected yet, so we
                        // automatically set the expected signatories to include the customer.
                        formMethods.setValue(
                          'expectedSignatories',
                          [{ role: 'customer', identifier: 'customer' }],
                          { shouldDirty: true }
                        );
                      }
                      onChange(template);
                    }}
                  />
                );
              }}
            />

            {templateHtml && (
              <div className="flex gap-4">
                <Button
                  variant="secondary"
                  onClick={() => downloadHtmlFile(templateHtml.raw, name)}
                >
                  <UntitledIcon name="download-01.3" className="w-4 h-4" />
                  Download
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    formMethods.setValue('templateHtml', null, {
                      shouldDirty: true,
                    });
                  }}
                >
                  <UntitledIcon name="x-close" className="w-2.5 h-2.5" />
                  Clear
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <div>
        <div className="mb-2">
          <H2>Available variables</H2>
        </div>
        <Card>
          <Card.Body>
            <AvailableDocumentTemplateVariablesList
              input={{
                expectedSignatories: expectedSignatories ?? null,
              }}
            />
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};
